import _ from 'lodash'
import traverse from 'traverse'
import { PartialDeep, UnknownRecord } from 'type-fest'

import { DeepBoolean } from 'types/object'
import { deepRemoveNestedEmptyObjects, hasAnyNestedObject } from 'utils/object'
import { REPORTED_KEY_VALUE_MAP } from './sections/constants'

import type {
  AbTestsCommonSchema,
  VariantKey,
  Variants,
  VariantsSchema,
} from './abTestsCommonSchema'
import type { FixedOptions } from './types'
import type {
  AbTestOption,
  AbTestParametersSchema,
} from 'features/ab-tests/types/abTestParametersSchema'
import type { AbTestVariantsSchema, Label } from 'features/ab-tests/types/abTestVariantsSchema'
import type { UseFormSetValue } from 'react-hook-form'
import type { TraverseContext } from 'traverse'

export const checkParameters = ({
  parameters,
  variant,
}: {
  parameters: AbTestParametersSchema
  variant: VariantsSchema
}): AbTestParametersSchema => {
  const tempParameters: AbTestParametersSchema = structuredClone(parameters)

  traverse(variant, {
    immutable: true,
  }).forEach(function (value) {
    const { isLeaf, path }: TraverseContext = this

    if (isLeaf) {
      if (_.isObject(value) && _.isEmpty(value)) return

      for (let i = 1; i <= path.length; i++) {
        const deepeningPath: string[] = path.slice(0, i)

        const correspondingParameter: AbTestOption = _.get(tempParameters, deepeningPath)
        if (correspondingParameter === undefined) return

        _.set(tempParameters, deepeningPath, {
          ...correspondingParameter,
          isChecked: true,
          isCollapsed: false,
        })
      }
    }
  })

  return tempParameters
}

export const filterSelectedParameters = (
  parameters: AbTestParametersSchema,
): DeepBoolean<AbTestParametersSchema> =>
  deepRemoveNestedEmptyObjects(
    traverse(parameters, { immutable: true }).map(function (node) {
      const { delete: deleteNode, update: updateNode }: TraverseContext = this

      if (typeof node === 'object' && hasAnyNestedObject(node)) {
        updateNode(_.omit(node, ['label', 'isCollapsed', 'isChecked']))
      } else {
        const { isChecked }: AbTestOption = node

        if (isChecked) {
          updateNode(isChecked)
        } else {
          deleteNode()
        }
      }
    }),
  )

export const filterSchemaByParameters = ({
  filteredParameters,
  schema,
}: {
  filteredParameters: DeepBoolean<AbTestParametersSchema>
  schema: AbTestVariantsSchema
}): PartialDeep<AbTestVariantsSchema> => {
  const filteredSchema: PartialDeep<AbTestVariantsSchema> = {}

  traverse(filteredParameters).forEach(function (node) {
    const { path }: TraverseContext = this

    if (node === true) {
      const subtree: PartialDeep<AbTestVariantsSchema> = _.get(schema, path)
      _.set(filteredSchema, path, subtree)

      for (let i = 1; i < path.length; i++) {
        const parentPath: string[] = path.slice(0, i)
        const label: Label = _.get(schema, [...parentPath, 'label'])

        _.set(filteredSchema, [...parentPath, 'label'], label)
      }
    }
  })

  return deepRemoveNestedEmptyObjects(filteredSchema)
}

export const filterVariantsByParameters = ({
  parameters,
  variants,
}: {
  parameters: PartialDeep<AbTestParametersSchema>
  variants: Variants
}): Variants => {
  const filteredVariants: Variants = structuredClone(variants)

  traverse(variants, {
    immutable: true,
  }).forEach(function (value) {
    const { isLeaf, path }: TraverseContext = this

    if (isLeaf) {
      if (_.isObject(value) && _.isEmpty(value)) return

      const pathToParameter = path.slice(1, -1)
      if (pathToParameter.length === 0) return

      const { isChecked }: AbTestOption = _.get(parameters, pathToParameter)
      if (isChecked === false) {
        _.unset(filteredVariants, path)
      }
    }
  })

  return deepRemoveNestedEmptyObjects(filteredVariants)
}

export const mergeFixedOptions = (
  variantsSchema: AbTestVariantsSchema,
  filteredVariants: null | UnknownRecord,
): Partial<AbTestVariantsSchema> => {
  const { reportedKeyValue, trafficShare }: FixedOptions = variantsSchema

  return {
    trafficShare,
    // eslint-disable-next-line perfectionist/sort-objects
    reportedKeyValue,
    ...filteredVariants,
  }
}

export const extractFormValues = (
  schema: PartialDeep<AbTestVariantsSchema>,
  variant: 'value' | 'workspaceDefaultValue' = 'value',
): VariantsSchema => {
  const keysToExclude: string[] = [
    'isChecked',
    'isCollapsed',
    'label',
    'type',
    'options',
    variant === 'value' ? 'workspaceDefaultValue' : 'value',
  ]

  const traverseSchema = (node: UnknownRecord): UnknownRecord => {
    if (typeof node === 'object' && node !== null) {
      if (variant in node) {
        return node[variant] as UnknownRecord
      }

      const result: UnknownRecord = {}

      for (const key of Object.keys(node)) {
        if (!keysToExclude.includes(key)) {
          result[key] = traverseSchema(node[key] as UnknownRecord)
        }
      }

      return result
    }

    return node
  }

  return traverseSchema(schema) as VariantsSchema
}

export const setVariants = ({
  defaultFormValues,
  onSetValue,
  variants,
}: {
  defaultFormValues: VariantsSchema
  onSetValue: UseFormSetValue<AbTestsCommonSchema>
  variants: undefined | Variants
}): void => {
  if (variants === undefined) {
    const formVariants: Variants = {
      variantA: { ...defaultFormValues, trafficShare: 50 },
      variantB: { ...defaultFormValues, trafficShare: 50 },
    } as Variants
    onSetValue('variants', reassignDefaultReportedKeyValues(formVariants))
  } else {
    onSetValue('variants', reassignFormValues(variants, defaultFormValues), {
      shouldDirty: true,
    })
  }
}

export const reassignFormValues = (variants: Variants, options: VariantsSchema) => {
  const updatedVariants = {} as Variants

  Object.entries(variants).forEach(
    ([variantKey, variantData]: [string, undefined | VariantsSchema]): void => {
      const tempVariantOptions: undefined | VariantsSchema = structuredClone(variantData)

      _.mergeWith(tempVariantOptions, options, mergeCustomizer)
      updatedVariants[variantKey as keyof Variants] = tempVariantOptions
    },
  )

  return updatedVariants
}

const mergeCustomizer = (destination: UnknownRecord, source: UnknownRecord) => {
  if (typeof destination === 'undefined') return source

  if (typeof source === 'object') {
    return _.mergeWith(_.pick(destination, Object.keys(source)), source, mergeCustomizer)
  }

  return destination
}

export const reassignDefaultReportedKeyValues = (formValues: Variants): Variants =>
  Object.fromEntries(
    Object.entries(formValues)
      .map(([key, value]: [string, undefined | VariantsSchema]): [string, VariantsSchema] => [
        key,
        {
          ...value,
          reportedKeyValue: REPORTED_KEY_VALUE_MAP[key as VariantKey],
        },
      ])
      .sort(variantsSortComparator),
  ) as Variants

export const rearrangeTrafficShare = (variants: PartialDeep<Variants>): Variants => {
  const variantsCount: number = Object.keys(variants).length

  const baseValue: number = parseFloat(_.round(100 / variantsCount, 2).toFixed(1))
  const distribution: number[] = Array(variantsCount).fill(baseValue)

  const total: number = baseValue * variantsCount
  const remainder: number = _.round(100 - total, 2)

  distribution[variantsCount - 1] += remainder
  distribution.map((value: number): number => _.round(value, 2))

  type Entry = [key: string, value: PartialDeep<VariantsSchema>]
  const updatedVariants = Object.fromEntries(
    Object.entries(variants).map(
      ([key, value]: Entry, index: number): Entry => [
        key,
        {
          ...value,
          trafficShare: distribution[index],
        },
      ],
    ),
  ) as Variants

  return updatedVariants
}

export const variantsSortComparator = (
  [keyA]: [key: string, value?: unknown],
  [keyB]: [key: string, value?: unknown],
): number => keyA.toLowerCase().localeCompare(keyB.toLowerCase())
